import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import apiService from "../../services/api_services";
import auth_service from "../../services/auth_service";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faCity, faGlobe, faHome, faUser } from "@fortawesome/free-solid-svg-icons";
import SkeletonLoading from "../../components/SkeletonLoading";

const AddAddress = ({ isOpen, onClose, isEditing, editingAddress, onSave }) => {
  const customerId = auth_service.getAccessTokenData()?.id;
  const [address, setAddress] = useState({
    customerId,
    addressName: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    country: "",
    pincode: "",
    phoneNumber: "", // Add phoneNumber field
    id: "",
  });
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Dispatch event to hide bottom nav
      window.dispatchEvent(new Event('modalOpen'));
      // Optional: Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Dispatch event to show bottom nav
      window.dispatchEvent(new Event('modalClose'));
      // Re-enable body scroll when modal is closed
      document.body.style.overflow = 'auto';
    }
    
    // Cleanup function
    return () => {
      window.dispatchEvent(new Event('modalClose'));
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isEditing && editingAddress) {
      setAddress({ ...editingAddress, customerId }); // Populate address for editing
    } else {
      resetForm();
    }
  }, [isEditing, editingAddress]);
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.4,
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    },
    exit: { opacity: 0, y: -50, transition: { duration: 0.3 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.3 } },
  };

  const resetForm = () => {
    setAddress({
      customerId,
      addressName: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
      phoneNumber: "", // Reset phoneNumber field
      id: "",
    });
    setErrors({});
  };

  const validate = () => {
    const newErrors = {};
    const { addressName, addressLine1, pincode, city, state, country, phoneNumber } = address;

    if (!addressName) newErrors.addressName = "Address name is required.";
    if (!addressLine1) newErrors.addressLine1 = "Address line 1 is required.";
    if (!pincode) newErrors.pincode = "Pincode is required.";
    else if (!/^\d{5,6}$/.test(pincode)) newErrors.pincode = "Pincode must be 5-6 digits.";
    if (!city) newErrors.city = "City is required.";
    if (!state) newErrors.state = "State is required.";
    if (!country) newErrors.country = "Country is required.";
    if (!phoneNumber) newErrors.phoneNumber = "Phone number is required.";
    else if (!/^\d{10}$/.test(phoneNumber)) newErrors.phoneNumber = "Phone number must be 10 numerics.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddress({ ...address, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    try {
      let response;
      const addressToSave = isEditing ? { ...address, id: address.id } : address;

      if (isEditing) {
        response = await apiService.editAddress(addressToSave);
        if (response?.success) {
          toast.success("Address edited successfully", {
            duration: 3000,
            position: "top-center"
          });
        }
      } else {
        response = await apiService.addAddress(addressToSave);
        if (response?.success) {
          toast.success("Address added successfully", {
            duration: 3000,
            position: "top-right"
          });
        }
      }
      onSave();
      onClose();
      resetForm();
    } catch (err) {
      toast.error("Failed to add address", {
        duration: 3000,
        position: "top-right"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex ${
        isOpen ? "visible opacity-100" : "invisible opacity-0"
      } transition-opacity duration-300`}
    >
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose}></div>

      {/* Modal */}
      <motion.div
        className={`relative w-full max-w-md h-full bg-white shadow-lg transform transition-transform duration-300 overflow-y-auto ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxHeight: "100vh", position: "absolute", right: 0 }}
        initial="hidden"
        animate={isOpen ? "visible" : "hidden"}
        exit="exit"
        variants={containerVariants}
      >
        {/* Header */}
        <motion.div 
          className="sticky top-0 bg-white z-10 p-4 border-b flex justify-between items-center"
          variants={itemVariants}
          style={{
            background: 'linear-gradient(to right, #001A6E, #0055A4)',
            color: 'white',
            borderTopLeftRadius: '12px',
            borderTopRightRadius: '12px'
          }}
        >
          <h2 className="text-lg font-semibold">
            {isEditing ? "Edit Address" : "Add New Address"}
          </h2>
          <motion.button
            className="text-white hover:text-gray-200"
            onClick={onClose}
            aria-label="Close"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            ✕
          </motion.button>
        </motion.div>

        {/* Form */}
        <div className="p-4">
          {isLoading ? (
            <div style={{ padding: '20px' }}>
              <SkeletonLoading type="address" />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {[
                { label: "Address Name", name: "addressName", icon: faUser },
                { label: "Address Line 1", name: "addressLine1", icon: faHome },
                { label: "Address Line 2", name: "addressLine2", icon: faHome },
                { label: "City", name: "city", icon: faCity },
                { label: "State", name: "state", icon: faMapMarkerAlt },
                { label: "Country", name: "country", icon: faGlobe },
                { label: "Pincode", name: "pincode", icon: faMapMarkerAlt },
                { label: "Phone Number", name: "phoneNumber", icon: faUser },
              ].map(({ label, name, icon }) => (
                <motion.div key={name} className="form-group mb-4" variants={itemVariants}>
                  <label className="block font-medium text-gray-700 mb-2">
                    <FontAwesomeIcon icon={icon} style={{ marginRight: '8px', color: '#001A6E' }} />
                    {label}:
                  </label>
                  <input
                    type={name === "phoneNumber" ? "tel" : "text"}
                    name={name}
                    value={address[name]}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:border-blue-500"
                    disabled={isSaving}
                  />
                  {errors[name] && (
                    <motion.p 
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors[name]}
                    </motion.p>
                  )}
                </motion.div>
              ))}
              <motion.button
                type="submit"
                className="w-full bg-[#1F509A] text-white px-4 py-2 rounded hover:bg-[#1F509A]/90 transition duration-300 flex justify-center items-center"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isEditing ? "Saving..." : "Adding..."}
                  </>
                ) : (
                  isEditing ? "Save Changes" : "Save Address"
                )}
              </motion.button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AddAddress;